import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CrawlJob, PropertyContentCatalog, PropertyContentCatalogItem } from "../../../shared/crawl";
import { api } from "../lib/api";

type CatalogResponse = { data: PropertyContentCatalog };
type CatalogItemResponse = { data: PropertyContentCatalogItem };
type CrawlResponse = { data: CrawlJob };

export function usePropertyContentCatalog(brandId: string, surfaceId: string) {
  return useQuery({
    queryKey: ["property-content", brandId, surfaceId],
    queryFn: () => api.get<CatalogResponse>(`/api/brands/${brandId}/surfaces/${surfaceId}/content`),
    refetchInterval: (query) => {
      const status = query.state.data?.data.latest_status;
      return status === "queued" || status === "crawling" ? 2_000 : false;
    },
  });
}

export function usePropertyContentItem(brandId: string, surfaceId: string, itemId: string | null) {
  return useQuery({
    queryKey: ["property-content-item", brandId, surfaceId, itemId],
    queryFn: () => api.get<CatalogItemResponse>(`/api/brands/${brandId}/surfaces/${surfaceId}/content/${itemId}`),
    enabled: Boolean(itemId),
  });
}

export function useDiscoverPropertyContent(
  brandId: string,
  surfaceId: string,
  canonicalUrl: string,
  propertyKind: "website" | "bluesky" = "website",
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CrawlResponse>(`/api/brands/${brandId}/crawl`, {
      type: propertyKind === "bluesky" ? "social" : "web",
      url: canonicalUrl,
      surface_id: surfaceId,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["property-content", brandId, surfaceId] }),
  });
}

export function useRefreshPropertyContent(brandId: string, surfaceId: string, jobId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CrawlResponse>(`/api/brands/${brandId}/crawl/${jobId}/refresh`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["property-content", brandId, surfaceId] }),
  });
}

export function useReprocessPropertyPage(brandId: string, surfaceId: string, sourceUrl: string) {
  return useMutation({
    mutationFn: () => api.post<CrawlResponse>(`/api/brands/${brandId}/crawl`, {
      type: "web",
      url: sourceUrl,
      surface_id: surfaceId,
      scope: "page",
    }),
  });
}

export function useCrawlJob(brandId: string, jobId: string | null) {
  return useQuery({
    queryKey: ["crawl-job", brandId, jobId],
    queryFn: () => api.get<CrawlResponse>(`/api/brands/${brandId}/crawl/${jobId}`),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.data.status;
      return status === "queued" || status === "crawling" ? 2_000 : false;
    },
  });
}
